import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaSync, FaLayerGroup } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Student {
  firstName: string;
  lastName: string;
  email: string;
}

interface Batch {
  id: number | string;
  name: string;
  topic?: string;
  startingDate?: string;
  endingDate?: string;
  isActive?: boolean;
  mentorName?: string;
  students?: Student[];
}

const ManageBatches = () => {
  const [activeBatches, setActiveBatches] = useState<Batch[]>([]);
  const [inactiveBatches, setInactiveBatches] = useState<Batch[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedBatchId, setExpandedBatchId] = useState<
    number | string | null
  >(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/batches`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch batches");
      const data = await res.json();
      const arrayData = Array.isArray(data) ? data : data.batches || [];

      const mapped = arrayData.map((b: any) => ({
        id: b.id,
        name: b.name,
        topic: b.topic || "",
        startingDate: b.startingDate,
        endingDate: b.endingDate || "",
        isActive: b.isActive,
        mentorName: b.mentor
          ? [b.mentor.firstName, b.mentor.lastName].filter(Boolean).join(" ")
          : "",
        students: Array.isArray(b.students)
          ? b.students.map((s: any) => ({
              firstName: s.firstName,
              lastName: s.lastName,
              email: s.email,
            }))
          : [],
      }));

      setActiveBatches(mapped.filter((b: { isActive: any }) => b.isActive));
      setInactiveBatches(mapped.filter((b: { isActive: any }) => !b.isActive));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id: any) => {
    if (!window.confirm("Are you sure you want to delete this batch?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/batches/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete batch");
      setActiveBatches((prev) => prev.filter((b) => b.id !== id));
      setInactiveBatches((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      alert("Failed to delete batch.");
    }
  };

  const toggleStudentDropdown = (id: string | number) => {
    setExpandedBatchId(expandedBatchId === id ? null : id);
  };

  const renderBatchTable = (title: string, batchList: Batch[]) => (
    <div className="mb-10">
      <h3 className="text-xl font-bold mb-3 text-[#009fe3]">{title}</h3>
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 font-sans">
            <thead className="bg-[#009fe3] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Topic
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Mentor
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Students
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {batchList.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="py-10 text-center text-gray-400 font-semibold"
                  >
                    No batches found.
                  </td>
                </tr>
              ) : (
                batchList.map((batch) => (
                  <tr
                    key={batch.id}
                    className="hover:bg-[#e0f7ff]/40 transition"
                  >
                    <td className="px-4 py-3 font-medium">{batch.id}</td>
                    <td className="px-4 py-3 font-bold text-[#009fe3]">
                      {batch.name}
                    </td>
                    <td className="px-4 py-3">
                      {batch.topic || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {batch.startingDate ? (
                        new Date(batch.startingDate).toLocaleDateString()
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {batch.endingDate ? (
                        new Date(batch.endingDate).toLocaleDateString()
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {batch.isActive ? (
                        <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          Active
                        </span>
                      ) : (
                        <span className="inline-block bg-gray-200 text-gray-500 px-3 py-1 rounded-full text-xs font-semibold">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {batch.mentorName || (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleStudentDropdown(batch.id)}
                        className="text-[#009fe3] hover:text-[#007cb5] font-semibold flex items-center transition-colors"
                      >
                        {expandedBatchId === batch.id ? (
                          <FiChevronUp />
                        ) : (
                          <FiChevronDown />
                        )}{" "}
                        View
                      </button>
                      {expandedBatchId === batch.id &&
                        (batch.students ?? []).length > 0 && (
                          <ul className="mt-2 text-sm text-gray-700 list-disc list-inside">
                            {(batch.students ?? []).map((s, idx) => (
                              <li key={idx}>
                                {s.firstName} {s.lastName} ({s.email})
                              </li>
                            ))}
                          </ul>
                        )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            navigate(`/admin/edit-batch/${batch.id}`)
                          }
                          className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-4 py-1 rounded-lg font-semibold shadow transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(batch.id)}
                          className="bg-red-100 hover:bg-red-200 text-red-600 px-4 py-1 rounded-lg font-semibold shadow transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaLayerGroup />}
        title="Manage Batches"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Batches" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#009fe3] tracking-tight">
            Course Batches
          </h2>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
            >
              <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <Link
              to="/admin/create-batch"
              className="bg-[#009fe3] hover:bg-[#007cb5] text-white font-semibold px-5 py-2 rounded-lg shadow transition-colors text-base w-full sm:w-auto text-center"
            >
              + Create Batch
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading batches...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={fetchData}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {renderBatchTable("Active Batches", activeBatches)}

            <div className="text-center">
              <button
                onClick={() => setShowInactive(!showInactive)}
                className="bg-[#009fe3] hover:bg-[#007cb5] text-white font-semibold border border-[#009fe3] hover:border-[#007cb5] px-5 py-2 rounded-lg transition-colors shadow"
              >
                {showInactive
                  ? "Hide Inactive Batches"
                  : "Show Inactive Batches"}
              </button>
            </div>

            {showInactive &&
              renderBatchTable("Inactive Batches", inactiveBatches)}
          </>
        )}
      </main>
    </div>
  );
};

export default ManageBatches;
